import { queryOptions } from "@tanstack/react-query";
import type { LegacyHubMigrationStatusResponse, LegacyHubMigrationSummary } from "@nexus/contracts";
import {
  fetchLegacyHubMigrationPreview,
  fetchLegacyHubMigrationStatus,
} from "@/features/opportunities/api/legacy-hub-migration.api";

/** A migration copies row by row, so a few seconds between polls is plenty. */
const LEGACY_HUB_MIGRATION_POLL_INTERVAL_MS = 2_000;

// ─── Query Keys ──────────────────────────────────────────────────────

export const legacyHubMigrationKeys = {
  all: ["legacy-hub-migration"] as const,
  preview: (tenantId: string) => ["legacy-hub-migration", "preview", tenantId] as const,
  status: (tenantId: string) => ["legacy-hub-migration", "status", tenantId] as const,
};

// ─── Query Options ───────────────────────────────────────────────────

/** Dry-run counts. Fetched only while the confirm dialog is open; always fresh. */
export function legacyHubMigrationPreviewQueryOptions(tenantId: string, enabled: boolean) {
  return queryOptions<LegacyHubMigrationSummary, Error>({
    queryKey: legacyHubMigrationKeys.preview(tenantId),
    queryFn: fetchLegacyHubMigrationPreview,
    enabled: enabled && !!tenantId,
    staleTime: 0,
    gcTime: 0,
  });
}

/** The tenant's latest run. Polls while one is going, so the menu and dialog follow it. */
export function legacyHubMigrationStatusQueryOptions(tenantId: string, enabled: boolean) {
  return queryOptions<LegacyHubMigrationStatusResponse, Error>({
    queryKey: legacyHubMigrationKeys.status(tenantId),
    queryFn: fetchLegacyHubMigrationStatus,
    enabled: enabled && !!tenantId,
    refetchInterval: (query) => (query.state.data?.running ? LEGACY_HUB_MIGRATION_POLL_INTERVAL_MS : false),
  });
}
